import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LeagueCreationData, ValidationError, AwardData, PayoutType } from '../../../types/league';

interface StepProps {
  data: Partial<LeagueCreationData>;
  updateData: (data: Partial<LeagueCreationData>) => void;
  errors: ValidationError[];
  addError: (field: string, message: string) => void;
  clearErrors: () => void;
}

const AwardsStep: React.FC<StepProps> = ({ data, updateData, errors, addError, clearErrors }) => {
  const { t } = useTranslation();
  const [newAward, setNewAward] = useState<AwardData>({
    name: '',
    type: 'money',
    amount: 0,
    item_desc: '',
    per_date: false,
    overall: false
  });

  const handleAddAward = () => {
    if (!newAward.name) {
      addError('awards', 'Award name is required');
      return;
    }

    if (newAward.type === 'money' && (!newAward.amount || newAward.amount <= 0)) {
      addError('awards', 'Amount must be greater than 0 for money awards');
      return;
    }

    if (newAward.type === 'item' && !newAward.item_desc) {
      addError('awards', 'Item description is required for item awards');
      return;
    }

    if (!newAward.per_date && !newAward.overall) {
      addError('awards', 'Award must be either per-date or overall (or both)');
      return;
    }

    const awards = data.awards || [];
    const awardToAdd = {
      ...newAward,
      amount: newAward.type === 'item' ? undefined : newAward.amount,
      item_desc: newAward.type === 'money' ? undefined : newAward.item_desc
    };

    updateData({
      awards: [...awards, awardToAdd]
    });

    setNewAward({
      name: '',
      type: 'money',
      amount: 0,
      item_desc: '',
      per_date: false,
      overall: false
    });
    clearErrors();
  };

  const handleRemoveAward = (index: number) => {
    const awards = data.awards || [];
    const updatedAwards = awards.filter((_, i) => i !== index);
    updateData({ awards: updatedAwards });
  };

  const getFieldError = (field: string) => {
    return errors.find(e => e.field === field)?.message;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('admin.createLeague.steps.awardsStep.title')}</h2>
        <p className="text-purple-200">
          {t('admin.createLeague.steps.awardsStep.subtitle')}
        </p>
      </div>

      {/* Existing Awards */}
      {data.awards && data.awards.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">{t('admin.createLeague.steps.awardsStep.currentAwards')}</h3>
          {data.awards.map((award, index) => (
            <div key={index} className="bg-white/10 rounded-lg p-4 border border-white/20">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-semibold text-white">{award.name}</h4>
                  
                  <div className="mt-2 space-y-1 text-sm">
                    <div className="text-purple-200">
                      <span className="font-medium">{t('admin.createLeague.steps.awardsStep.type')}</span> {' '}
                      {award.type === 'money' 
                        ? t('admin.createLeague.steps.awardsStep.cashAward', { amount: award.amount?.toFixed(2) })
                        : t('admin.createLeague.steps.awardsStep.item', { item: award.item_desc })
                      }
                    </div>
                    
                    <div className="text-purple-200">
                      <span className="font-medium">{t('admin.createLeague.steps.awardsStep.awarded')}</span> {' '}
                      {award.per_date && award.overall 
                        ? t('admin.createLeague.steps.awardsStep.eachWeekAndOverall')
                        : award.per_date 
                        ? t('admin.createLeague.steps.awardsStep.eachWeek')
                        : t('admin.createLeague.steps.awardsStep.overallOnly')
                      }
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => handleRemoveAward(index)}
                  className="text-red-400 hover:text-red-300 ml-4"
                >
                  {t('admin.createLeague.steps.awardsStep.remove')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Award */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.awardsStep.addNewAward')}</h3>
        
        <div className="space-y-4">
          {/* Award Name */}
          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.awardsStep.awardName')} *
            </label>
            <input
              type="text"
              value={newAward.name}
              onChange={(e) => setNewAward({ ...newAward, name: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t('admin.createLeague.steps.awardsStep.awardNamePlaceholder')}
            />
          </div>

          {/* Award Type and Value */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.awardsStep.awardType')} *
              </label>
              <select
                value={newAward.type}
                onChange={(e) => setNewAward({ ...newAward, type: e.target.value as PayoutType })}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="money" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.payoutTypes.money')}</option>
                <option value="item" className="bg-gray-800">{t('admin.createLeague.steps.leaderboardStep.payoutTypes.item')}</option>
              </select>
            </div>

            {newAward.type === 'money' ? (
              <div>
                <label className="block text-sm font-medium text-purple-200 mb-2">
                  {t('admin.createLeague.steps.awardsStep.amount')} *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newAward.amount || 0}
                  onChange={(e) => setNewAward({ ...newAward, amount: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            ) : (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-purple-200 mb-2">
                  {t('admin.createLeague.steps.awardsStep.itemDescription')} *
                </label>
                <input
                  type="text"
                  value={newAward.item_desc || ''}
                  onChange={(e) => setNewAward({ ...newAward, item_desc: e.target.value })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={t('admin.createLeague.steps.awardsStep.itemPlaceholder')}
                />
              </div>
            )}
          </div>

          {/* Award Frequency */}
          <div>
            <label className="block text-sm font-medium text-purple-200 mb-3">
              {t('admin.createLeague.steps.awardsStep.whenGiven')} *
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newAward.per_date}
                  onChange={(e) => setNewAward({ ...newAward, per_date: e.target.checked })}
                  className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
                />
                <span className="ml-2 text-purple-200">{t('admin.createLeague.steps.awardsStep.perDate')}</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newAward.overall}
                  onChange={(e) => setNewAward({ ...newAward, overall: e.target.checked })}
                  className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
                />
                <span className="ml-2 text-purple-200">{t('admin.createLeague.steps.awardsStep.overall')}</span>
              </label>
            </div>
            <p className="text-xs text-purple-300 mt-2">
              {t('admin.createLeague.steps.awardsStep.frequencyNote')}
            </p>
          </div>

          <button
            onClick={handleAddAward}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200"
          >
            {t('admin.createLeague.steps.awardsStep.addAward')}
          </button>

          {getFieldError('awards') && (
            <p className="text-red-400 text-sm mt-2">{getFieldError('awards')}</p>
          )}
        </div>
      </div>

      {/* Award Examples */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.awardsStep.commonAwardIdeas')}</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-purple-100 mb-3">{t('admin.createLeague.steps.awardsStep.performanceAwards')}</h4>
            <div className="space-y-2 text-purple-200 text-sm">
              <div className="p-2 bg-white/5 rounded">
                <div className="font-medium">{t('admin.createLeague.steps.awardsStep.performance300.title')}</div>
                <div className="text-xs">{t('admin.createLeague.steps.awardsStep.performance300.description')}</div>
              </div>
              <div className="p-2 bg-white/5 rounded">
                <div className="font-medium">{t('admin.createLeague.steps.awardsStep.performance800.title')}</div>
                <div className="text-xs">{t('admin.createLeague.steps.awardsStep.performance800.description')}</div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-purple-100 mb-3">{t('admin.createLeague.steps.awardsStep.funRecognition')}</h4>
            <div className="space-y-2 text-purple-200 text-sm">
              <div className="p-2 bg-white/5 rounded">
                <div className="font-medium">{t('admin.createLeague.steps.awardsStep.mostImproved.title')}</div>
                <div className="text-xs">{t('admin.createLeague.steps.awardsStep.mostImproved.description')}</div>
              </div>
              <div className="p-2 bg-white/5 rounded">
                <div className="font-medium">{t('admin.createLeague.steps.awardsStep.strikeKing.title')}</div>
                <div className="text-xs">{t('admin.createLeague.steps.awardsStep.strikeKing.description')}</div>
              </div>
              
            </div>
          </div>
        </div>
      </div>

      {/* Award Frequency Guide */}
      <div className="bg-blue-500/20 border border-blue-400 rounded-lg p-4">
        <h4 className="font-semibold text-blue-100 mb-2">{t('admin.createLeague.steps.awardsStep.frequencyGuide.title')}</h4>
        <div className="text-blue-200 text-sm space-y-1">
          <div><strong>{t('admin.createLeague.steps.awardsStep.frequencyGuide.perDateLabel')}</strong> {t('admin.createLeague.steps.awardsStep.frequencyGuide.perDateDesc')}</div>
          <div><strong>{t('admin.createLeague.steps.awardsStep.frequencyGuide.overallLabel')}</strong> {t('admin.createLeague.steps.awardsStep.frequencyGuide.overallDesc')}</div>
          <div><strong>{t('admin.createLeague.steps.awardsStep.frequencyGuide.bothLabel')}</strong> {t('admin.createLeague.steps.awardsStep.frequencyGuide.bothDesc')}</div>
        </div>
      </div>

      {(!data.awards || data.awards.length === 0) && (
        <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-4 text-center">
          <p className="text-yellow-200">
            {t('admin.createLeague.steps.awardsStep.noAwardsWarning')}
          </p>
        </div>
      )}
    </div>
  );
};

export default AwardsStep;